import { useCallback, useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import { enqueueSnackbar } from 'notistack'
import { api } from 'src/api/root'
import { handleApiError } from 'src/enviroment/api/errorHandler'
import { MAX_FILE_SIZE_BYTES } from 'src/enviroment/constants/constants'
import { formatFileSize } from 'src/enviroment/lib/formatters/format-file-size'
import { buildFolderUploadPlan } from 'src/enviroment/utils/buildFolderUploadPlan'
import { describeUploadResult } from 'src/enviroment/utils/describeUploadResult'
import { splitUploadableFiles } from 'src/enviroment/utils/splitUploadableFiles'
import { DataRoomUploadResult, FolderUploadPlan } from 'src/types/projects'

const reportUploadResult = (result: DataRoomUploadResult, retryHint: string) => {
  const { message, variant } = describeUploadResult(result, retryHint)
  enqueueSnackbar(message, { variant })
}

// `done` is null until the folders exist: the storage check and folder POSTs have no per-file count
const toProgressLabel = ({ done, total }: { done: number | null; total: number }): string => {
  if (done === null) return 'Preparing…'
  return done < total ? `Uploading ${done} / ${total}` : 'Finishing…'
}

const useBeforeUnloadGuard = (active: boolean) => {
  useEffect(() => {
    if (!active) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = true
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [active])
}

/** Data room uploads — plain files and whole folders; hosted by the dialog so opening a document does not end them. */
export const useDataRoomUpload = (projectId: string, isOpen: boolean) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [folderPlan, setFolderPlan] = useState<FolderUploadPlan | null>(null)
  const [folderProgress, setFolderProgress] = useState<{ done: number | null; total: number } | null>(null)
  const [isStorageLimitOpen, setIsStorageLimitOpen] = useState(false)
  const isUploading = pendingFiles.length > 0 || folderProgress !== null
  useBeforeUnloadGuard(isUploading)

  const { data: storageUsage } = api.project.dataRoom.storageUsage.useQuery({ enabled: isOpen })
  const { mutateAsync: upload } = api.project.dataRoom.upload.useMutation({
    onError: (error) => {
      // validate-storage endpoint always returns 400 when storage limit is exceeded
      const isStorageLimitError = error instanceof AxiosError && error.response?.status === 400
      if (isStorageLimitError) {
        setIsStorageLimitOpen(true)
      } else {
        handleApiError(error)
      }
    },
  })

  const uploadFiles = useCallback(
    (incoming: File[], folderPath: string | null) => {
      const { valid, oversized } = splitUploadableFiles(incoming)
      if (oversized.length > 0) {
        enqueueSnackbar(
          `${oversized.map((f) => f.name).join(', ')} exceeds the ${formatFileSize(MAX_FILE_SIZE_BYTES)} limit.`,
          { variant: 'error' },
        )
      }
      if (valid.length === 0) return

      setPendingFiles((prev) => [...valid, ...prev])
      upload({ projectId, uploads: valid.map((file) => ({ file, folderPath })) })
        .then((result) => {
          if (result.uploaded < valid.length || result.indexingFailed) {
            reportUploadResult(result, 'Try uploading them again.')
          }
        })
        .catch(() => undefined) // onError has already reported it
        .finally(() => setPendingFiles((prev) => prev.filter((f) => !valid.includes(f))))
    },
    [projectId, upload],
  )

  const planFolder = useCallback(
    (files: File[], targetPath: string | null) => {
      const plan = buildFolderUploadPlan(files, targetPath)
      if (plan.uploads.length === 0) {
        enqueueSnackbar(
          `Nothing to upload: the folder has no supported files up to ${formatFileSize(MAX_FILE_SIZE_BYTES)}.`,
          { variant: 'warning' },
        )
        return
      }
      if (storageUsage?.limitBytes != null && storageUsage.usedBytes + plan.totalBytes > storageUsage.limitBytes) {
        setIsStorageLimitOpen(true)
        return
      }
      setFolderPlan(plan)
    },
    [storageUsage],
  )

  const confirmFolder = useCallback(async () => {
    if (!folderPlan) return
    const { uploads, folderPaths } = folderPlan
    const total = uploads.length
    setFolderPlan(null)
    setFolderProgress({ done: null, total })
    const result = await upload({
      projectId,
      uploads,
      folderPaths,
      onProgress: (done) => setFolderProgress({ done, total }),
    }).catch(() => null) // onError has already reported it
    setFolderProgress(null)
    if (result) reportUploadResult(result, 'Upload the folder again to retry.')
  }, [folderPlan, projectId, upload])

  const cancelFolder = useCallback(() => setFolderPlan(null), [])
  const closeStorageLimit = useCallback(() => setIsStorageLimitOpen(false), [])

  return {
    pendingFiles,
    folderPlan,
    folderProgressLabel: folderProgress && toProgressLabel(folderProgress),
    isUploading,
    storageLimit: isStorageLimitOpen && storageUsage ? storageUsage : null,
    uploadFiles,
    planFolder,
    confirmFolder,
    cancelFolder,
    closeStorageLimit,
  }
}
